import React, { useEffect, useMemo, useState } from 'react';
import classnames from 'classnames/bind';
import { useRouter } from 'next/router';
import { Typography, Rating, ProgressBar } from 'gd-design-library';
import { ProductReview, ProductReviewSummary } from '@services';
import { Button } from '@components';
import { WriteReviewModal } from '../WriteReviewModal/WriteReviewModal';
import styles from './ProductDetailsReviews.module.scss';

const INITIAL_VISIBLE_REVIEWS = 3;
const REVIEWS_PAGE_SIZE = 7;

const CN = 'product-details-reviews';
const containerClassName = `${CN}__container`;
const summaryClassName = `${CN}__summary`;
const mainInfoClassName = `${CN}__main-info`;
const ratingsDistributionClassName = `${CN}__ratings-distribution`;
const ratingsDistributionItemClassName = `${CN}__ratings-distribution-item`;
const ratingsDistributionItemSelectedClassName = `${CN}__ratings-distribution-item--selected`;
const ratingsDistributionItemProgressBarClassName = `${CN}__ratings-distribution-item-progress-bar`;
const listClassName = `${CN}__list`;
const itemClassName = `${CN}__item`;
const metaClassName = `${CN}__meta`;
const contentClassName = `${CN}__content`;
const loadMoreClassName = `${CN}__load-more`;
const cx = classnames.bind(styles);

interface ProductDetailsReviewsProps {
  reviewSummary?: ProductReviewSummary;
  reviews?: ProductReview[];
  productId: string;
}

export const ProductDetailsReviews: React.FC<ProductDetailsReviewsProps> = ({
  reviewSummary,
  reviews,
  productId
}) => {
  const router = useRouter();
  const [ visibleCount, setVisibleCount ] = useState(INITIAL_VISIBLE_REVIEWS);
  const [ selectedRating, setSelectedRating ] = useState<number | null>(null);
  const [ isWriteReviewModalOpen, setIsWriteReviewModalOpen ] = useState(false);
  const [ reviewSubmitted, setReviewSubmitted ] = useState(false);

  const handleCloseModal = (): void => {
    setIsWriteReviewModalOpen(false);
    if (reviewSubmitted) {
      router.replace(router.asPath);
    }
  };

  const filteredReviews = useMemo(() => {
    if (!reviews) {
      return [];
    }
    if (selectedRating === null) {
      return reviews;
    }

    return reviews.filter(review => review.rating === selectedRating);
  }, [ reviews, selectedRating ]);

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_REVIEWS);
  }, [ reviews ]);

  const visibleReviews = useMemo(
    () => filteredReviews.slice(0, visibleCount),
    [ filteredReviews, visibleCount ]
  );

  const handleSeeMoreReviews = (): void => {
    setVisibleCount(c => c + REVIEWS_PAGE_SIZE);
  };

  const handleRatingRowClick = (rating: number): void => {
    setSelectedRating(prev => (prev === rating ? null : rating));
  };

  const hasReviews = reviews && reviews.length > 0;

  return (
    <div className={ cx(CN) } id='reviews'>
      <div className={ cx(`${CN}__header`) }>
        <Typography variant='h4'>Ratings & Reviews</Typography>
        <Button variant='secondary' onClick={ () => setIsWriteReviewModalOpen(true) }>
          Write a Review
        </Button>
      </div>
      <WriteReviewModal
        isOpen={ isWriteReviewModalOpen }
        onClose={ handleCloseModal }
        productId={ productId }
        onReviewSubmitted={ () => setReviewSubmitted(true) }
      />
      <div className={ cx(containerClassName) }>
        {hasReviews && reviewSummary ? (
          <>
            <div className={ cx(summaryClassName) }>
              <div className={ cx(mainInfoClassName) }>
                <Typography variant='h1' style={ { lineHeight: '0.9' } }>{reviewSummary.averageRating.toFixed(1)}</Typography>
                <Rating value={ reviewSummary.averageRating } readOnly />
                <Typography variant='small' as='p'>{reviewSummary.reviewCount} ratings & reviews</Typography>
              </div>
              <div className={ cx(ratingsDistributionClassName) }>
                {[ 5, 4, 3, 2, 1 ].map(rating => {
                  const reviewCount = reviews.filter(review => review.rating === rating).length;

                  const isSelected = selectedRating === rating;

                  return (
                    <button
                      key={ rating }
                      type='button'
                      className={ cx(
                        ratingsDistributionItemClassName,
                        { [ratingsDistributionItemSelectedClassName]: isSelected }
                      ) }
                      onClick={ () => handleRatingRowClick(rating) }
                      disabled={ reviewCount === 0 }
                      aria-pressed={ isSelected }
                      aria-label={ `Show ${rating}-star reviews, ${reviewCount} total` }
                    >
                      <Typography variant='small' as='p'>{rating} star{rating > 1 ? 's' : ''}</Typography>
                      <ProgressBar
                        value={ reviewCount > 0 ? reviewCount / reviewSummary.reviewCount * 100 : 0.0001 }
                        className={ cx(ratingsDistributionItemProgressBarClassName) }
                      />
                      <Typography variant='small' as='p'>{reviewCount}</Typography>
                    </button>
                  );
                })}
                {selectedRating !== null && (
                  <Button
                    variant='inherit'
                    onClick={ () => setSelectedRating(null) }
                    style={ { alignSelf: 'flex-start', textTransform: 'none' } }
                  >
                  Show all
                  </Button>
                )}
              </div>
            </div>
            <div className={ cx(listClassName) }>
              {visibleReviews.map(review => (
                <div key={ `${review.authorName}-${review.date}` } className={ cx(itemClassName) }>
                  <div className={ cx(metaClassName) }>
                    <Rating value={ review.rating } readOnly size='sm' />
                    <Typography variant='strong'>{review.authorName}</Typography>
                    <Typography variant='caption' as='p'>{review.date}</Typography>
                  </div>
                  <div className={ cx(contentClassName) }>
                    <Typography variant='strong'>{review.title}</Typography>
                    <Typography>{review.comment}</Typography>
                  </div>
                </div>
              ))}
              {visibleCount < filteredReviews.length && (
                <Button
                  variant='outlined'
                  onClick={ handleSeeMoreReviews }
                  className={ cx(loadMoreClassName) }
                >
                See more reviews
                </Button>
              )}
            </div>
          </>
        ) : (
          <Typography as='p'>There are no reviews for this product yet.</Typography>
        )}
      </div>
    </div>
  );
};
